import {
  collection,
  getDocs,
  query,
  doc,
  updateDoc,
  deleteDoc,
  addDoc,
  DocumentData,
} from 'firebase/firestore';
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { db, storage } from '../firebase/firebase';
import { ProductModel } from '../model/productModel';

const COL = 'Products';
const colRef = collection(db, COL);

// ── Storage helpers ──────────────────────────────────────────────

export async function uploadEcomImage(file: File, docId: string, slot: string): Promise<string> {
  const path = `ecom_products/${docId}_${slot}_${file.name}`;
  const imgRef = storageRef(storage, path);
  await uploadBytes(imgRef, file);
  return getDownloadURL(imgRef);
}

export async function deleteStorageUrl(url: string): Promise<void> {
  if (!url || !url.includes('firebasestorage')) return;
  try {
    const match = url.match(/\/o\/(.+?)\?/);
    if (!match) return;
    await deleteObject(storageRef(storage, decodeURIComponent(match[1])));
  } catch (e) {
    console.warn('Storage delete skipped:', e);
  }
}

// ── CRUD ─────────────────────────────────────────────────────────

export const fetchProducts = async (): Promise<ProductModel[]> => {
  try {
    const snapshot = await getDocs(query(colRef));
    return snapshot.docs.map(d => {
      const data = d.data();
      if (data.createdAt?.toDate) data.createdAt = data.createdAt.toDate();
      if (data.updatedAt?.toDate) data.updatedAt = data.updatedAt.toDate();
      if (Array.isArray(data.reviews)) {
        data.reviews = data.reviews.map((r: any) =>
          r.reviewDate?.toDate ? { ...r, reviewDate: r.reviewDate.toDate() } : r
        );
      }
      return { id: d.id, ...data } as ProductModel;
    });
  } catch (error) {
    console.error('Failed to fetch products:', error);
    return [];
  }
};

export const createProduct = async (productData: Omit<ProductModel, 'id'>): Promise<string> => {
  try {
    const docRef = await addDoc(colRef, productData as DocumentData);
    return docRef.id;
  } catch (error) {
    console.error('Failed to create product:', error);
    throw error;
  }
};

export const updateProduct = async (productId: string, updateData: Partial<ProductModel>) => {
  try {
    await updateDoc(doc(db, COL, productId), updateData as DocumentData);
  } catch (error) {
    console.error('Failed to update product:', error);
    throw error;
  }
};

export const deleteProduct = async (
  productId: string,
  primaryImage?: string,
  images?: string[],
): Promise<void> => {
  try {
    await deleteDoc(doc(db, COL, productId));
    if (primaryImage) await deleteStorageUrl(primaryImage);
    for (const url of images ?? []) await deleteStorageUrl(url);
  } catch (error) {
    console.error('Failed to delete product:', error);
    throw error;
  }
};
